import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  CircularProgress,
  IconButton,
  Button,
  ButtonBase,
  Typography,
  Chip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { patientService } from "@/services";
import { Patient } from "@/resources/types";
import { cn } from "@/lib/utils";
import { scrollable } from "@/components/scrollable";
import { Text } from "@imminently/react/ui/core/text";
import { ExternalRegisterDot } from "@/components/externalregister/ExternalRegisterDot";
import { getPatientExternalRegisterAlerts, type PatientWithExternalRegisterSummary } from "@/components/externalregister/patientSummary";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { canRenderExternalRegisterDotIndicator } from "@/util/externalRegister";
import { getShortSex } from "@/global";

// Encounter statuses that are considered "still active" and can be resumed
const ACTIVE_ENCOUNTER_STATUSES = ["draft", "submitted", "triaged", "seen", "expected"];

// Require at least 2 characters for both firstName and lastName
const MIN_SEARCH_LENGTH = 2;

interface ExistingPatientSearchOverlayProps {
  firstName: string;
  lastName: string;
  onSelectPatient: (patient: Patient) => void;
  onSelectEncounter: (patient: Patient, encounter: any) => void;
  onClose: () => void;
  loadingEncounterId?: string | null;
  onLoadingChange?: (isLoading: boolean) => void;
  onOpenExternalRegisterDetails?: (patientId: string, alertId?: string) => void;
}

// Helper function to format encounter time information (without status)
// Defensive: handles invalid/missing dates gracefully
const formatEncounterTime = (encounter: any, t: (key: string, options?: Record<string, unknown>) => string): string => {
  try {
    if (encounter.status === "expected") {
      // For expected encounters, use the arrived time (epoch) instead of created
      if (!encounter.arrived) return t("patient.search.encounterTime.expected");
      const arrivedTime = moment(encounter.arrived);
      if (!arrivedTime.isValid()) return t("patient.search.encounterTime.expected");
      return t("patient.search.encounterTime.expectedAt", { time: arrivedTime.fromNow() });
    } else {
      // For other statuses, use the created time (ISO string)
      if (!encounter.created) return t("patient.search.encounterTime.unknownTime");
      const createdTime = moment(encounter.created);
      if (!createdTime.isValid()) return t("patient.search.encounterTime.unknownTime");
      return createdTime.fromNow();
    }
  } catch {
    return t("patient.search.encounterTime.unknownTime");
  }
};

// Helper to find the most recent active encounter from a list
const findMostRecentActiveEncounter = (encounters: any[]): any | null => {
  if (!encounters || encounters.length === 0) return null;
  // Encounters are already sorted by lastModified desc, so first active one is most recent
  return encounters.find((enc) => ACTIVE_ENCOUNTER_STATUSES.includes(enc.status)) || null;
};

// Helper function to map encounter status to display text
const getStatusDisplayText = (status: string | null | undefined, t: (key: string) => string): string => {

  const statusStr = String(status || "").trim();
  if (!statusStr) return "";

  const statusMap: Record<string, string> = {
    disabled: t("patient.search.status.archived"),
    didnotpresent: t("patient.search.status.didNotAttend"),
    pendingDischarged: t("patient.search.status.pendingDischarge"),
  };

  if (statusMap[statusStr]) {
    return statusMap[statusStr];
  }

  // Capitalize first letter for unmapped statuses
  return statusStr.charAt(0).toUpperCase() + statusStr.slice(1);
};

const getPatientDemographicsText = (patient: Patient, t: (key: string, options?: Record<string, unknown>) => string) => {
  const demographics = [];
  if (patient.dateOfBirth && moment(patient.dateOfBirth).isValid()) {
    demographics.push(`${t("patient.search.dob.prefix")}${moment(patient.dateOfBirth).format("DD/MM/YYYY")}`);
  }

  demographics.push(t("patient.search.sex.prefix", { sex: getShortSex({ patient } as any) }));

  return demographics.join(" | ");
};

const ExistingPatientSearchOverlay: React.FC<ExistingPatientSearchOverlayProps> = ({
  firstName,
  lastName,
  onSelectPatient,
  onSelectEncounter,
  onClose,
  loadingEncounterId,
  onLoadingChange,
  onOpenExternalRegisterDetails,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [debouncedFirstName, setDebouncedFirstName] = useState("");
  const [debouncedLastName, setDebouncedLastName] = useState("");
  const [debouncedEnabled, setDebouncedEnabled] = useState(false);
  const { isConciergeMode } = useConciergeMode();
  const { settings } = useSettingsContext();
  const showExternalRegisterDot = canRenderExternalRegisterDotIndicator({
    isConciergeMode,
    settings,
  });
  const onLoadingChangeRef = React.useRef(onLoadingChange);

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  // Track if user manually closed the overlay and the search values at that time
  const [dismissedSearch, setDismissedSearch] = useState<{ firstName: string; lastName: string } | null>(null);

  /**
   * Track whether we've completed a query for the current debounced parameters.
   *
   * This flag prevents a flicker issue caused by React Query's caching behavior:
   * - React Query returns cached results from previous searches immediately
   * - When user types, we debounce (400ms) before updating search params
   * - During this debounce, cached results from OLD search params can briefly appear
   * - This causes the overlay to flash with stale/incorrect patient results
   *
   * Solution: Only show the overlay after we've confirmed the fetch completed
   * for the CURRENT search parameters, not stale cached data.
   */
  const [hasQueriedForCurrentParams, setHasQueriedForCurrentParams] = useState(false);

  // Debounce the search to avoid excessive API calls
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;

    const enabled = Boolean(
      firstName && lastName &&
      firstName.trim().length >= MIN_SEARCH_LENGTH &&
      lastName.trim().length >= MIN_SEARCH_LENGTH
    );

    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      setDebouncedFirstName(firstName || "");
      setDebouncedLastName(lastName || "");
      setDebouncedEnabled(enabled);
    }, 400);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [firstName, lastName]);

  const { data, isLoading, isFetching } = patientService.useGetList(
    debouncedEnabled ? {
      meta: {
        firstName: `*${debouncedFirstName}*`,
        lastName: `*${debouncedLastName}*`,
        sort: {
          field: "lastModified",
          order: "desc",
        },
        $incRecentEncounters: true,
      },
    } as any : null
  );

  const patients = (data?.data ?? []) as PatientWithExternalRegisterSummary[];

  // Track when we've actually queried for the current parameters
  useEffect(() => {
    if (debouncedEnabled && debouncedFirstName && debouncedLastName) {
      // Reset the flag when parameters change
      setHasQueriedForCurrentParams(false);
    }
  }, [debouncedEnabled, debouncedFirstName, debouncedLastName]);

  // Mark as queried when fetch completes
  useEffect(() => {
    if (!isFetching && debouncedEnabled && debouncedFirstName && debouncedLastName) {
      setHasQueriedForCurrentParams(true);
    }
  }, [isFetching, debouncedEnabled, debouncedFirstName, debouncedLastName, patients.length]);

  // Notify parent about loading state changes
  useEffect(() => {
    const hasSearchConditions = Boolean(
      firstName && lastName &&
      firstName.trim().length >= MIN_SEARCH_LENGTH && lastName.trim().length >= MIN_SEARCH_LENGTH
    );

    onLoadingChangeRef.current?.(hasSearchConditions && (isLoading || isFetching));
  }, [firstName, lastName, isLoading, isFetching]);

  useEffect(() => {
    return () => {
      onLoadingChangeRef.current?.(false);
    };
  }, []);

  // Handle manual close - track the search values when dismissed
  const handleClose = () => {
    onLoadingChangeRef.current?.(false);
    setDismissedSearch({
      firstName: firstName?.trim() || "",
      lastName: lastName?.trim() || "",
    });
    setIsVisible(false);
    onClose();
  };

  // Helper to check if search was dismissed for current values
  const isDismissedForCurrentSearch = (): boolean => {
    if (dismissedSearch === null) return false;
    const firstNameTrimmed = firstName?.trim() || "";
    const lastNameTrimmed = lastName?.trim() || "";
    return dismissedSearch.firstName === firstNameTrimmed && dismissedSearch.lastName === lastNameTrimmed;
  };

  // Clear dismissal when search values change
  useEffect(() => {
    const firstNameTrimmed = firstName?.trim() || "";
    const lastNameTrimmed = lastName?.trim() || "";
    if (dismissedSearch !== null &&
        (dismissedSearch.firstName !== firstNameTrimmed || dismissedSearch.lastName !== lastNameTrimmed)) {
      setDismissedSearch(null);
    }
  }, [firstName, lastName, dismissedSearch]);

  // Control visibility - only show when there are results (not during loading)
  // Don't show if user dismissed it for the current search values
  useEffect(() => {
    const hasSearchConditions = Boolean(
      firstName && lastName &&
      firstName.trim().length >= MIN_SEARCH_LENGTH && lastName.trim().length >= MIN_SEARCH_LENGTH
    );

    const wasDismissed = isDismissedForCurrentSearch();

    // Only show overlay if we have search conditions AND results (not loading) AND we've queried for current params
    // AND the user hasn't dismissed it for this search
    const shouldShow = hasSearchConditions && !isLoading && !isFetching && patients.length > 0 && hasQueriedForCurrentParams && !wasDismissed;

    setIsVisible(shouldShow);
  }, [firstName, lastName, isLoading, isFetching, patients.length, debouncedFirstName, debouncedLastName, hasQueriedForCurrentParams, dismissedSearch]);

  // Don't render anything if not visible
  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        maxHeight: "400px",
        overflow: "hidden",
        mt: 0.5,
        width: "fit-content",
        minWidth: "400px",
        maxWidth: "calc(100vw - 32px)",
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 2,
      }}
    >
      <Stack sx={{ p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Text variant="subtitle2" sx={{ fontWeight: 600 }}>{t("patient.search.title")}</Text>
          <IconButton size="small" onClick={handleClose} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            maxHeight: "320px",
            overflowY: "auto",
          }}
          className={cn(scrollable)}
        >
          <Stack spacing={0.5}>
            {patients.map((patient, index) => (
              <Box
                key={patient.id}
                sx={{
                  p: 1.5,
                  backgroundColor: index % 2 === 0 ? "grey.50" : "background.paper",
                  borderRadius: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack spacing={1}>
                  {/* Patient Name Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{ flexWrap: "nowrap" }}>
                    <Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {patient.firstName} {patient.lastName}
                        </Typography>
                        {showExternalRegisterDot ? (
                          (() => {
                            const alerts = getPatientExternalRegisterAlerts(patient);
                            const canOpenDetails = !!patient.id && alerts.length > 0 && !!onOpenExternalRegisterDetails;
                            const dot = <ExternalRegisterDot alerts={alerts} />;
                            if (!canOpenDetails) return dot;

                            return (
                              <ButtonBase
                                component="button"
                                type="button"
                                aria-label={t("patient.status.clinicalAlerts.viewDetails")}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onOpenExternalRegisterDetails(patient.id, alerts.length === 1 ? alerts[0].id : undefined);
                                }}
                                sx={{
                                  border: 0,
                                  background: "transparent",
                                  p: 0.25,
                                  borderRadius: 0.5,
                                  lineHeight: 0,
                                  verticalAlign: "middle",
                                }}
                              >
                                {dot}
                              </ButtonBase>
                            );
                          })()
                        ) : null}
                      </Stack>
                      {(() => {
                        const demographicsText = getPatientDemographicsText(patient, t);
                        return demographicsText ? (
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {demographicsText}
                          </Typography>
                        ) : null;
                      })()}
                      {(patient as any).recentEncounters && (patient as any).recentEncounters.length > 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, whiteSpace: "nowrap" }}>
                          {t("patient.search.recentEncounters.label")}
                        </Typography>
                      ) : null}
                    </Stack>
                    {(() => {
                      const mostRecentActive = findMostRecentActiveEncounter((patient as any).recentEncounters);

                      return (
                        <Stack spacing={1} sx={{ flexShrink: 0 }}>
                          {mostRecentActive ? (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => onSelectEncounter(patient, mostRecentActive)}
                              disabled={loadingEncounterId === mostRecentActive.id}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "none",
                                minWidth: "auto",
                                px: 1.5,
                                py: 0.5,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              {loadingEncounterId === mostRecentActive.id ? (
                                <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexWrap: "nowrap" }}>
                                  <CircularProgress size={12} />
                                  {t("patient.search.buttons.loading")}
                                </Stack>
                              ) : (
                                t(mostRecentActive.status === "expected" ? "patient.search.buttons.resumeExpects" : "patient.search.buttons.resume")
                              )}
                            </Button>
                          ) : null}
                          <Button
                            variant={mostRecentActive ? "text" : "outlined"}
                            size="small"
                            onClick={() => onSelectPatient(patient)}
                            sx={{
                              fontSize: mostRecentActive ? "0.625rem" : "0.75rem",
                              textTransform: "none",
                              px: mostRecentActive ? 0.75 : 1.5,
                              py: mostRecentActive ? 0 : 0.5,
                              height: mostRecentActive ? "20px" : undefined,
                              lineHeight: mostRecentActive ? 1 : undefined,
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            {t("patient.search.buttons.createNew")}
                          </Button>
                        </Stack>
                      );
                    })()}
                  </Stack>

                  {/* Recent Encounters */}
                  {(patient as any).recentEncounters && (patient as any).recentEncounters.length > 0 ? (() => {
                    const recentEncounters = (patient as any).recentEncounters;

                    return (
                      <Box>
                        <Stack spacing={0.5}>
                          {recentEncounters.map((encounter: any, encounterIndex: number) => {
                            return (
                              <Stack
                                key={encounterIndex}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                gap={2}
                                sx={{
                                  p: 0.75,
                                  height: "51px",
                                  backgroundColor: "background.paper",
                                  borderRadius: 0.25,
                                  border: "1px solid",
                                  borderColor: "grey.200",
                                  flexWrap: "nowrap",
                                }}
                              >
                                <Stack direction="row" gap={1} alignItems="center" sx={{ flexWrap: "nowrap" }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                    {formatEncounterTime(encounter, t)}
                                  </Typography>
                                  <Chip
                                    label={getStatusDisplayText(encounter.status, t)}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      height: "20px",
                                      fontSize: "0.65rem",
                                      borderColor: "divider",
                                      color: "text.secondary",
                                      borderRadius: 0.5,
                                      "& .MuiChip-label": {
                                        px: 0.75,
                                        py: 0,
                                      },
                                    }}
                                  />
                                </Stack>
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Box>
                    );
                  })() : (
                    <Typography variant="caption" color="text.secondary">
                      {t("patient.search.recentEncounters.none")}
                    </Typography>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default ExistingPatientSearchOverlay;
